// Personal libs
import { IUser } from '@shared/interface/model.interface';
import { Repository } from './common/repository.class';
import {
  apiAddUser,
  apiDeleteUser,
  apiGetUsers,
  apiPasswordRenew,
  apiPutUpdateUserRoles,
  apiResendValidationLink,
  apiUpdateUser,
} from '@data/datasource/remote/admin.api';
import { IApiResponse } from '@shared/interface/api-response.interface';
import { ApiError } from '@shared/error/api.error';
import {
  dbUpdateUserRoles,
  dbGetUsers,
  dbSetUsers,
} from '@data/datasource/local/user.database';
import { RepositoryStrategyEnum } from '@shared/enum/repository-strategy.enum';
import { sessionManager } from '@shared/service/sessionManager/session-manager.class';

class AdminRepository extends Repository {
  constructor() {
    super();
  }

  /*****************************************
   *
   * GET FUNCTIONS
   *
   *****************************************/
  // Get all users
  getUsers(
    forcedStrategy?: RepositoryStrategyEnum,
  ): Promise<IUser[] | undefined> {
    const finalStrategy = forcedStrategy ?? this.repositoryStragtegy;

    if (finalStrategy === RepositoryStrategyEnum.LOCAL_FIRST) {
      return dbGetUsers().then((users) => {
        if (users.length === 0) {
          return apiGetUsers().then((u) => {
            dbSetUsers(u);
            return u;
          });
        } else {
          apiGetUsers().then((users) => dbSetUsers(users));

          return users;
        }
      });
    } else if (finalStrategy === RepositoryStrategyEnum.LOCAL_ONLY) {
      return dbGetUsers();
    } else {
      return apiGetUsers()
        .then((progs) => {
          dbSetUsers(progs);
          return progs;
        })
        .catch(() => dbGetUsers());
    }
  }

  /*****************************************
   *
   * PUT FUNCTIONS
   *
   *****************************************/
  // Update user roles
  updateUserRoles(
    user: IUser,
  ): Promise<IApiResponse<string, string> | undefined> {
    return apiPutUpdateUserRoles(user)
      .then((response) => {
        if (sessionManager.currentUser.value?.uuid === user.uuid) {
          // Update session users roles
          sessionManager.updateUserRoles(user?.roles)

          // Update local database
          dbUpdateUserRoles(user);
        }

        return response;
      })
      .catch((err: ErrorConstructor) => {
        if (err instanceof ApiError) {
          if (
            err.code === 'ADMIN_UPDATE_USER_ERR_01' ||
            err.code === 'ADMIN_UPDATE_USER_ERR_04' ||
            err.code === 'ADMIN_UPDATE_USER_ERR_05'
          ) {
            throw new Error(
              'Action interdite avec les rôles que vous possédez',
            );
          } else if (err.code === 'ADMIN_UPDATE_USER_ERR_02') {
            throw new Error('Données fournies incorrectes');
          } else if (err.code === 'ADMIN_UPDATE_USER_ERR_03') {
            throw new Error('Utilisateur inconnu');
          } else if (err.code === 'ADMIN_UPDATE_USER_ERR_06') {
            throw new Error(
              "Erreur technique lors de la modification des rôles de l'utilisateur",
            );
          }
        } else {
          throw new Error('Erreur inconnue');
        }
        return Promise.resolve(undefined);
      });
  }

  // Update user
  updateUser(user: IUser): Promise<string | undefined> {
    return apiUpdateUser(user).catch((err: ErrorConstructor) => {
      if (err instanceof ApiError) {
        if (err.code === 'ADMIN_UPDATE_USER_ERR_01') {
          throw new Error('Action interdite avec les rôles que vous possédez');
        } else if (err.code === 'ADMIN_UPDATE_USER_ERR_02') {
          throw new Error('Données fournies incorrectes');
        } else if (err.code === 'ADMIN_UPDATE_USER_ERR_03') {
          throw new Error('Utilisateur inconnu');
        } else if (err.code === 'ADMIN_UPDATE_USER_ERR_04') {
          throw new Error('Action interdite');
        } else if (err.code === 'ADMIN_UPDATE_USER_ERR_05') {
          throw new Error("Nom d'utilisateur invalide");
        } else if (err.code === 'ADMIN_UPDATE_USER_ERR_06') {
          throw new Error("Ce nom d'utilisateur existe déjà");
        } else if (err.code === 'ADMIN_UPDATE_USER_ERR_07') {
          throw new Error(
            "Erreur technique lors de la modification de l'utilisateur",
          );
        }
      } else {
        throw new Error('Erreur inconnue');
      }
      return Promise.resolve(undefined);
    });
  }

  /*****************************************
   *
   * POST FUNCTIONS
   *
   *****************************************/
  // Update user roles
  addUser(
    username?: string,
    email?: string,
  ): Promise<IApiResponse<string, string> | undefined> {
    return apiAddUser(username, email).catch((err: ErrorConstructor) => {
      if (err instanceof ApiError) {
        if (err.code === 'ADMIN_ADD_USER_ERR_01') {
          throw new Error('Action interdite avec les rôles que vous possédez');
        } else if (err.code === 'ADMIN_ADD_USER_ERR_02') {
          throw new Error('Données fournies incorrectes');
        } else if (err.code === 'ADMIN_ADD_USER_ERR_03') {
          throw new Error("L'utilisateur existe déjà");
        } else if (err.code === 'ADMIN_ADD_USER_ERR_04') {
          throw new Error(
            "Erreur technique lors de la création de l'utilisateur",
          );
        }
      } else {
        throw new Error('Erreur inconnue');
      }
      return Promise.resolve(undefined);
    });
  }

  // Update the user password
  passwordRenew(
    email?: string,
  ): Promise<IApiResponse<string, string> | undefined> {
    return apiPasswordRenew(email).catch((err: ErrorConstructor) => {
      if (err instanceof ApiError) {
        if (err.code === 'ADMIN_PWD_RENEW_USER_ERR_01') {
          throw new Error('Action interdite avec les rôles que vous possédez');
        } else if (err.code === 'ADMIN_PWD_RENEW_USER_ERR_02') {
          throw new Error('Données fournies incorrectes');
        } else if (err.code === 'ADMIN_PWD_RENEW_USER_ERR_03') {
          throw new Error('Utilisateur inconnu');
        } else if (err.code === 'ADMIN_PWD_RENEW_USER_ERR_04') {
          throw new Error(
            "Erreur technique lors de l'envoi du nouveau mot de passe à l'utilisateur",
          );
        } else if (err.code === 'ADMIN_PWD_RENEW_USER_ERR_05') {
          throw new Error(
            "Erreur technique lors de la modification du mot de passe de l'utilisateur",
          );
        }
      } else {
        throw new Error('Erreur inconnue');
      }
      return Promise.resolve(undefined);
    });
  }

  // Update user roles
  resendValidationLink(
    uuid?: string,
  ): Promise<IApiResponse<string, string> | undefined> {
    return apiResendValidationLink(uuid).catch((err: ErrorConstructor) => {
      if (err instanceof ApiError) {
        if (err.code === 'ADMIN_RESEND_VALIDATION_LINK_ERR_01') {
          throw new Error('Action interdite avec les rôles que vous possédez');
        } else if (err.code === 'ADMIN_RESEND_VALIDATION_LINK_ERR_02') {
          throw new Error('Données fournies incorrectes');
        } else if (err.code === 'ADMIN_RESEND_VALIDATION_LINK_ERR_03') {
          throw new Error("Erreur technique lors du processus de sauvegarde de l'utilisateur");
        } else if (err.code === 'ADMIN_RESEND_VALIDATION_LINK_ERR_04') {
          throw new Error("Erreur technique lors l'envoi de mail : domaine non trouvé");
        } else if (err.code === 'ADMIN_RESEND_VALIDATION_LINK_ERR_05') {
          throw new Error("Erreur technique lors l'envoi de mail");
        } else if (err.code === 'ADMIN_RESEND_VALIDATION_LINK_ERR_06') {
          throw new Error("Erreur technique lors l'envoi de mail");
        }
      } else {
        throw new Error('Erreur inconnue');
      }
      return Promise.resolve(undefined);
    });
  }

  /*****************************************
   *
   * DELETE FUNCTIONS
   *
   *****************************************/
  // Update user roles
  deleteUser(uuid?: string): Promise<string | undefined> {
    return apiDeleteUser(uuid).catch((err: ErrorConstructor) => {
      if (err instanceof ApiError) {
        if (err.code === 'ADMIN_DELETE_USER_ERR_01') {
          throw new Error('Action interdite avec les rôles que vous possédez');
        } else if (err.code === 'ADMIN_DELETE_USER_ERR_02') {
          throw new Error('Données fournies incorrectes');
        } else if (err.code === 'ADMIN_DELETE_USER_ERR_03') {
          throw new Error('Utilisateur inconnu');
        } else if (err.code === 'ADMIN_DELETE_USER_ERR_04') {
          throw new Error(
            'Impossible de supprimer un utilisateur qui il a déjà déposé des vidéos (validées ou non).<br>Possibilités : <ul><li>Supprimer ses vidéos puis le supprimer</li><li>le désactiver</li></ul>',
          );
        } else if (err.code === 'ADMIN_DELETE_USER_ERR_05') {
          throw new Error(
            'Impossible de supprimer un utilisateur qui est taggé dans au moins une vidéo (validées ou non).<br>Possibilités : <ul><li>Supprimer les vidéos où il est taggé puis le supprimer</li><li>le désactiver</li></ul>',
          );
        } else if (err.code === 'ADMIN_DELETE_USER_ERR_06') {
          throw new Error(
            "Erreur technique lors de la suppression de l'utilisateur",
          );
        }
      } else {
        throw new Error('Erreur inconnue');
      }
      return Promise.resolve(undefined);
    });
  }
}

export default new AdminRepository();
